import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Stack,
  Chip,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { DataGridPro } from "@mui/x-data-grid-pro";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import { getModelosTipo } from "../../api/Design/modelosTipoApi";
import DrawerModeloTipo from "./DrawerModeloTipo";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageKpiChipSx,
  pageMobileCardSx,
  pageDataGridSx,
} from "../../styles/pageModernStyles";

const isActivoRow = (row) => {
  const v = row?.ACTIVO;
  return v === "S" || v === true || v === 1;
};

const VolumenPrenda = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      minWidth: 0,
      width: "100%",
    }),
    [theme],
  );

  const toolbarIconSx = useMemo(
    () => ({
      border: "1px solid",
      borderColor: alpha(accent, 0.22),
      backgroundColor: theme.palette.background.paper,
      color: accent,
      width: { xs: 40, md: 34 },
      height: { xs: 40, md: 34 },
      minWidth: { xs: 40, md: 34 },
      minHeight: { xs: 40, md: 34 },
      p: 0,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
      "&:hover": {
        backgroundColor: alpha(accent, 0.1),
        borderColor: accent,
      },
      "& .MuiSvgIcon-root": { fontSize: "1.05rem" },
    }),
    [theme, accent],
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decodedToken = jwtDecode(token);
      if (decodedToken.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getModelosTipo();
      setRows(
        data.map((row, index) => ({
          id: row.ID_TIPO != null ? row.ID_TIPO : `row-${index}`,
          ...row,
        })),
      );
    } catch (err) {
      console.error("Error al obtener ModelosTipo:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => isActivoRow(r)).length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter(
      (row) =>
        row.TIPO?.toLowerCase().includes(search) ||
        row.ABREVIATURA?.toLowerCase().includes(search),
    );
  }, [rows, searchText]);

  const openRow = (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const handleRowClick = (params) => {
    openRow(params.row);
  };

  const handleAddNew = () => {
    setSelectedRow(null);
    setDrawerOpen(true);
  };

  const handleExportToExcel = () => {
    const dataToExport = filteredRows.map((row) => ({
      [t("VolumenPrendaPage.excelType")]: row.TIPO,
      [t("VolumenPrendaPage.excelAbbr")]: row.ABREVIATURA,
      [t("VolumenPrendaPage.excelCbm")]:
        row.VolumenUn != null && row.VolumenUn !== ""
          ? Number(row.VolumenUn).toFixed(4)
          : "",
      [t("VolumenPrendaPage.excelActive")]: isActivoRow(row)
        ? t("VolumenPrendaPage.activeYes")
        : t("VolumenPrendaPage.activeNo"),
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("VolumenPrendaPage.excelSheet"));

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `VolumenPrenda_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const formatCbm = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    return Number(v).toFixed(4);
  };

  const columns = useMemo(
    () => [
      {
        field: "TIPO",
        headerName: t("VolumenPrendaPage.colType"),
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "ABREVIATURA",
        headerName: t("VolumenPrendaPage.colAbbr"),
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value || "—"}
            size="small"
            sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
          />
        ),
      },
      {
        field: "VolumenUn",
        headerName: t("VolumenPrendaPage.colCbm"),
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
          >
            {formatCbm(params.value)}
          </Typography>
        ),
      },
      {
        field: "ACTIVO",
        headerName: t("VolumenPrendaPage.colActive"),
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const on = isActivoRow({ ACTIVO: params.value });
          return (
            <Chip
              size="small"
              label={
                on
                  ? t("VolumenPrendaPage.activeYes")
                  : t("VolumenPrendaPage.activeNo")
              }
              color={on ? "success" : "default"}
              variant={on ? "filled" : "outlined"}
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          );
        },
      },
    ],
    [t],
  );

  const renderMobileCard = (row) => {
    const active = isActivoRow(row);
    return (
      <Box
        key={row.id}
        onClick={() => openRow(row)}
        sx={{
          ...pageMobileCardSx(theme, {
            active,
            accentColor: active ? accent : theme.palette.text.secondary,
          }),
          cursor: "pointer",
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", lineHeight: 1.3 }}>
              {row.TIPO}
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.75 }}
            >
              <Chip
                label={row.ABREVIATURA || "—"}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
              />
              <Chip
                label={`CBM ${formatCbm(row.VolumenUn)}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.dark,
                }}
              />
              <Chip
                size="small"
                label={
                  active
                    ? t("VolumenPrendaPage.activeYes")
                    : t("VolumenPrendaPage.activeNo")
                }
                color={active ? "success" : "default"}
                variant={active ? "filled" : "outlined"}
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: { xs: "auto", md: "hidden" },
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        maxWidth: 1200,
        width: "100%",
        ...contentMargin,
      }}
    >
      <Box
        sx={{
          ...pageHeroShellSx(theme, { sticky: !isMobile }),
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack spacing={{ xs: 1.25, md: 1 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 1.25, md: 1 }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Box
                sx={{
                  width: { xs: 44, md: 40 },
                  height: { xs: 44, md: 40 },
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(accent, 0.22)
                      : "#d4f0f4",
                  color: theme.palette.primary.dark,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: { xs: 22, md: 20 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.05rem", md: "1.12rem" },
                  }}
                >
                  {t("sidebar.cbmGarment")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.35,
                    display: { xs: "block", md: "none" },
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                  }}
                >
                  {t("VolumenPrendaPage.heroSubtitle")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.5, rowGap: 0.35 }}
                >
                  <Chip
                    label={`${stats.total} ${t("VolumenPrendaPage.typesCount", { defaultValue: "tipos" })}`}
                    size="small"
                    variant="outlined"
                    sx={pageKpiChipSx}
                  />
                  <Chip
                    label={`${stats.active} ${t("VolumenPrendaPage.activeCount", { defaultValue: "activos" })}`}
                    size="small"
                    sx={{
                      ...pageKpiChipSx,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.dark,
                    }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                size="small"
                placeholder={t("VolumenPrendaPage.searchPlaceholder")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  width: { xs: "100%", md: 260 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={t("VolumenPrendaPage.exportExcel")}>
                <span>
                  <IconButton
                    onClick={handleExportToExcel}
                    aria-label={t("VolumenPrendaPage.exportExcel")}
                    sx={toolbarIconSx}
                  >
                    <DownloadOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("VolumenPrendaPage.addType")}>
                <span>
                  <IconButton
                    onClick={handleAddNew}
                    aria-label={t("VolumenPrendaPage.addType")}
                    sx={{
                      ...toolbarIconSx,
                      bgcolor: alpha(accent, 0.12),
                      borderColor: alpha(accent, 0.4),
                    }}
                  >
                    <AddOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          ...pageContentCardSx(theme, accent),
          p: 0,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {isMobile ? (
              <Stack spacing={1.25}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={72}
                    sx={{ borderRadius: 2.5 }}
                  />
                ))}
              </Stack>
            ) : (
              <Skeleton variant="rounded" height="100%" sx={{ minHeight: 320 }} />
            )}
          </Box>
        ) : filteredRows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              m: { xs: 1.5, md: 2 },
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2.5,
              borderStyle: "dashed",
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Inventory2OutlinedIcon
              sx={{ fontSize: 36, color: "action.disabled", mb: 1 }}
            />
            <Typography color="text.secondary" fontWeight={600}>
              {searchText.trim()
                ? t("VolumenPrendaPage.noSearchResults", {
                    defaultValue: "No hay tipos con esa búsqueda",
                  })
                : t("VolumenPrendaPage.noData", {
                    defaultValue: "No hay tipos de prenda registrados",
                  })}
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 1.5,
            }}
          >
            <Stack spacing={1.25}>
              {filteredRows.map((row) => renderMobileCard(row))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, height: "100%", width: "100%" }}>
            <DataGridPro
              rows={filteredRows}
              columns={columns}
              loading={loading}
              disableColumnFilter
              disableColumnMenu
              pageSizeOptions={[100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              hideFooter
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              sx={{
                ...pageDataGridSx(theme),
                height: "100%",
                border: "none",
                borderRadius: 0,
                cursor: "pointer",
              }}
            />
          </Box>
        )}
      </Box>

      <DrawerModeloTipo
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        modelo={selectedRow}
        onSave={fetchData}
      />
    </Box>
  );
};

export default VolumenPrenda;
